import React from "react";
import { Box, Divider, Typography, styled } from "@mui/material";
import { ItemName } from "./styles";
import type { MSHatData, SimpleNamedItem } from "../../types";

// --- Styled ---

const Heading = styled(Typography, {
  label: "EducationHeading",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 600,
  fontSize: "16px",
  lineHeight: "24px",
  color: "#ffffff",
  textAlign: "center",
  width: "100%",
}));

const Prompt = styled(Typography, {
  label: "EducationPrompt",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontStyle: "italic",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "22px",
  color: "#ffffff",
  textAlign: "center",
  width: "100%",
}));

const ColumnLabel = styled(Typography, {
  label: "EducationColumnLabel",
  shouldForwardProp: (prop) => prop !== "labelColor",
})<{ labelColor: string }>(({ labelColor }) => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 700,
  fontSize: "12px",
  lineHeight: "18px",
  textTransform: "uppercase",
  color: labelColor,
}));

// --- Helpers ---

const hasItems = (items?: SimpleNamedItem[]): boolean =>
  !!items && items.length > 0;

const Column = ({
  label,
  labelColor,
  items,
}: {
  label: string;
  labelColor: string;
  items?: SimpleNamedItem[];
}) => {
  if (!items || items.length === 0) return null;
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <ColumnLabel labelColor={labelColor} sx={{ mb: 1 }}>
        {label}
      </ColumnLabel>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {items.map((item) => (
          <Box
            key={item.id}
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            {item.icon?.url && (
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={item.icon.url}
                  alt=""
                  width={24}
                  height={24}
                  style={{ objectFit: "contain" }}
                />
              </Box>
            )}
            <ItemName>{item.name}</ItemName>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// --- Main component ---

export const EducationSection = ({ hat }: { hat: MSHatData }) => {
  const hasPriorities =
    hasItems(hat.msConnectedCourses) || hasItems(hat.msKeysToSuccess);
  const hasAfter = hasItems(hat.msPopularPaths) || hasItems(hat.msOtherRoutes);

  if (!hasPriorities && !hasAfter) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        alignItems: "center",
        width: "100%",
      }}
    >
      {hasPriorities && (
        <Box sx={{ width: "100%" }}>
          <Heading>High School Priorities</Heading>
          <Prompt>What’s my next step?</Prompt>
          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
            <Column
              label="Connected Courses"
              labelColor="#77ed8b"
              items={hat.msConnectedCourses}
            />
            <Column
              label="Keys to Success"
              labelColor="#ffd666"
              items={hat.msKeysToSuccess}
            />
          </Box>
        </Box>
      )}

      {hasPriorities && hasAfter && (
        <Divider
          sx={{ borderColor: "rgba(145, 158, 171, 0.2)", width: "100%" }}
        />
      )}

      {hasAfter && (
        <Box sx={{ width: "100%" }}>
          <Heading>After High School</Heading>
          <Prompt>What can I do after graduation?</Prompt>
          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
            <Column
              label="Popular Paths"
              labelColor="#efd6ff"
              items={hat.msPopularPaths}
            />
            <Column
              label="Other Routes"
              labelColor="#68cdf9"
              items={hat.msOtherRoutes}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};
